/*
 * R16H U1 (2026-10-12), reports/varun-2026-10-12/README.md section 4E: warn - never block - when a slot would scrape
 * nothing. Varun's slot25 had 601 "Enable Post Scraping" on and an EMPTY 613 "Source List"; the server issued one blank
 * source, the worker scraped "" four times a session (HikerAPI 422) and showed "Posts fetched: 0 / -30 / -60 / -90". The
 * server now drops a blank source (r16h S1), so this only tells the customer why nothing is scraped.
 *
 * The warning holds when ALL of these do (the same rule as V1's slot-settings2.html):
 *   - 601 Enable Post Scraping is on;
 *   - 613 Source List is empty (no non-blank entry when split on commas / new lines, as the server splits it), and list 2 holds
 *     no source either (R16H review: the server says so in sourceList2HasSources - list 2 has no setting id);
 *   - there is no other repost source: 664 Direct Post URLs is empty or 679 "Start Direct Urls Postings As Well" is off,
 *     677 "Schedule Posts From Fixed Folder" is off, 697 "Repost Posts From My Saved Collection" is off.
 * A setting the page does not show (a V1 gate: plan, posting off) counts as off / empty, as on V1, where it is not
 * rendered. Nothing here defines a setting: the ids are V1's, the field names come from FIELD_METADATA (697 is
 * registered from the server's metadata), the wording names V1's numbers.
 */

export const BLANK_SOURCE_TAG = '[R16H-BLANK-SOURCE]';

export const BLANK_SOURCE_IDS = Object.freeze({
  scraping: 601,
  sourceType: 612,
  sources: 613,
  directUrls: 664,
  fixedFolder: 677,
  directUrlsOn: 679,
  savedCollection: 697,
});

/** A checkbox value as the page holds it: true, 'true' (any case), 1, '1'. */
export function isTicked(v) {
  if (v === true || v === 1) return true;
  if (v === null || v === undefined || v === false) return false;
  const s = String(v).trim().toLowerCase();
  return s === 'true' || s === '1';
}

/*
 * R16H review (2026-10-13): one blank rule for the worker (W8), the server (S1 / S2) and both UIs - an entry of only blank characters
 * (whitespace, NBSP and the other Unicode spaces, zero-width spaces U+200B-U+200D, the word joiner U+2060, the BOM U+FEFF), or whose
 * target before '|' is blank ("|5", the worker's per-source count), names no source. JS's trim() keeps the zero-width spaces.
 */
const BLANKS = /[\s\u001C-\u001F\u200B-\u200D\u2060\uFEFF]/g;

/** An entry that names no source (the rule above). */
export function isBlankEntry(p) {
  return String(p).split('|')[0].replace(BLANKS, '') === '';
}

/** A list value with no non-blank entry when split on commas and new lines (the server's S1 rule). */
export function isBlankList(v) {
  if (v === null || v === undefined) return true;
  return String(v).split(/[,\r\n]/).every((p) => isBlankEntry(p));
}

/** The field name FIELD_METADATA gives a setting id, or null. */
export function fieldForId(id, fieldMetadata) {
  const entries = Object.entries(fieldMetadata || {});
  for (let i = 0; i < entries.length; i++) {
    const m = entries[i][1];
    if (m && m.number !== undefined && m.number !== null && Number(m.number) === Number(id)) return entries[i][0];
  }
  return null;
}

/**
 * The page's values for the ids above, keyed by id: `settings` is the page's form state (field-name keys). `loaded` is
 * the server's answer mapped to field names (the page's baseline): an id the server did not send is left out, so a
 * bundle default can never raise the warning. `hiddenIds` (a Set of ids hidden by V1's gates) are left out too.
 */
export function valuesById(settings, loaded, fieldMetadata, hiddenIds) {
  const out = {};
  if (!settings) return out;
  Object.values(BLANK_SOURCE_IDS).forEach((id) => {
    if (hiddenIds && hiddenIds.has && hiddenIds.has(id)) return;
    const field = fieldForId(id, fieldMetadata);
    if (!field) return;
    if (loaded && !Object.prototype.hasOwnProperty.call(loaded, field)) return;
    out[id] = settings[field];
  });
  return out;
}

function hint(type) {
  if (type === 'HASHTAGS') return 'hashtags (without #)';
  if (type === 'LOCATIONS') return 'location IDs';
  return 'numeric Instagram user IDs (not usernames)';
}

/**
 * null, or the warning: { title, text, logLine }. `byId` holds the values by setting id (valuesById); a missing 601 or 613
 * means "not known" and gives null. `facts` (R16H review, 2026-10-13): what the server says that is no setting -
 * `list2HasSources` (the settings answer's sourceList2HasSources): list 2 holds a source, as V1 and the server's S1 count it,
 * so there is something to scrape (true: no warning; false / missing: list 2 adds nothing - a server without the key warns as
 * r16h did). Pure.
 */
export function blankSourceWarning(byId, facts) {
  const v = byId || {};
  const I = BLANK_SOURCE_IDS;
  if (!Object.prototype.hasOwnProperty.call(v, I.scraping) || !Object.prototype.hasOwnProperty.call(v, I.sources)) return null;
  if (!isTicked(v[I.scraping])) return null;
  if (!isBlankList(v[I.sources])) return null;
  if (facts && facts.list2HasSources === true) return null;
  const directUrls = isTicked(v[I.directUrlsOn]) && !isBlankList(v[I.directUrls]);
  if (directUrls || isTicked(v[I.fixedFolder]) || isTicked(v[I.savedCollection])) return null;
  const type = v[I.sourceType] ? String(v[I.sourceType]) : '';
  return {
    title: '#601 is on, but #613 Source List is empty - nothing to scrape',
    text: '601 Enable Post Scraping is on, but 613 Source List is empty and no other repost source is set'
      + ' (664 Direct Post URLs with 679, 677 Fixed Folder, 697 Saved Collection), so there is nothing to scrape.'
      + ` Put ${hint(type)} in 613, separated by commas, or untick 601. Saving is not blocked.`,
    logLine: `${BLANK_SOURCE_TAG} 601 on, 613 empty, no other repost source (type ${type || 'not set'}) - warned, not blocked`,
  };
}
